/**
 * Client-side Awin Tracking Utilities
 * Mirrors ShopMy cookie handling pattern for Awin click tracking
 */

import { AwinTrackingCookies } from '@/types/Awin';

/**
 * Parse all browser cookies into an object
 * Mirrors the ShopMy cookie parsing pattern from SignupForm.tsx
 *
 * @returns Object with cookie names as keys and values as strings
 */
export function parseCookies(): AwinTrackingCookies {
  if (typeof document === 'undefined') {
    return {};
  }

  const cookies: AwinTrackingCookies = document.cookie
    .split(';')
    .reduce((result, cookie) => {
      const [key, value] = cookie.split('=');
      return {
        ...result,
        [(key || '').trim()]: (value || '').trim()
      };
    }, {});

  return cookies;
}

/**
 * Get Awin tracking cookies (awc and awc_time)
 * The 'awc' cookie is set by Awin's click tracking script
 *
 * @returns Object with Awin click tracking data
 */
export function getAwinTrackingData(): {
  awc?: string;
  awc_time?: string;
} {
  const cookies = parseCookies();
  return {
    awc: cookies.awc,
    awc_time: cookies.awc_time
  };
}

/**
 * Clear Awin tracking cookies after conversion is recorded
 * Mirrors ShopMy's cookie clearing pattern
 */
export function clearAwinTrackingCookies(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const now = new Date();
  const expires = now.toUTCString();

  // Clear awc cookie
  document.cookie = `awc=;expires=${expires};path=/;`;

  // Clear awc_time cookie
  document.cookie = `awc_time=;expires=${expires};path=/;`;
}

/**
 * Get Awin merchant ID from environment
 * @returns Awin merchant ID or null
 */
export function getAwinMerchantId(): string | null {
  return process.env.NEXT_PUBLIC_AWIN_MERCHANT_ID || null;
}

declare global {
  interface Window {
    AWIN?: {
      Tracking?: {
        AdvertiserConsent?: boolean;
        Consent?: {
          setAdvertiserConsentStatus?: (granted: boolean) => void;
        };
      };
    };
  }
}

/**
 * Sync Awin AdvertiserConsent to the given value.
 *
 * Why two writes: Awin reads `AWIN.Tracking.AdvertiserConsent` on script load
 * to decide whether to set the first-party cookie. After load, the runtime
 * exposes `setAdvertiserConsentStatus()` for updates. We always do both so the
 * value is correct whether this fires before or after the script is ready.
 */
export function setAwinAdvertiserConsent(granted: boolean): void {
  if (typeof window === 'undefined') return;

  window.AWIN = window.AWIN || {};
  window.AWIN.Tracking = window.AWIN.Tracking || {};
  window.AWIN.Tracking.AdvertiserConsent = granted;

  window.AWIN.Tracking.Consent?.setAdvertiserConsentStatus?.(granted);
}

/**
 * Load Awin tracking script (Master Tag)
 * This should be called on page load to initialize Awin click tracking
 *
 * @param merchantId - Awin merchant ID
 */
export function loadAwinTrackingScript(merchantId: string): void {
  if (typeof window === 'undefined') return;

  if (document.querySelector(`script[data-awin-merchant="${merchantId}"]`)) {
    return;
  }

  const script = document.createElement('script');
  script.src = `https://www.dwin1.com/${merchantId}.js`;
  script.async = true;
  script.setAttribute('data-awin-merchant', merchantId);
  script.type = 'text/javascript';

  script.onerror = () => {
    console.error('[AWIN Client] Failed to load MasterTag script:', script.src);
  };

  document.head.appendChild(script);
}

/**
 * Send conversion data to our internal API endpoint
 * This endpoint will then communicate with Awin's S2S API
 *
 * @param payload - Conversion data
 * @returns Promise with response
 */
export async function trackAwinConversion(payload: {
  userId: string;
  orderValue: number;
  currency: string;
  clickRef?: string;
  referralCode?: string;
  isNewCustomer: boolean;
  pageUrl: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch('/api/awin/track-conversion', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[Awin Client] Failed to track conversion:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}
